import { validGlassGoal } from "./stained-glass-themes";

export const GLASS_SHAPES = ["triangle", "rectangle", "diamond", "hexagon", "circle", "leaf", "shard", "pebble"] as const;
export type GlassShape = typeof GLASS_SHAPES[number];
export const GLASS_COLORS = [
  { name: "Ruby", hex: "#C53959" }, { name: "Amber", hex: "#E99E32" },
  { name: "Honey", hex: "#E4C14F" }, { name: "Emerald", hex: "#338F70" },
  { name: "Lagoon", hex: "#2697A0" }, { name: "Sapphire", hex: "#426AB4" },
  { name: "Violet", hex: "#8661AB" }, { name: "Rose", hex: "#D87996" },
  { name: "Seafoam", hex: "#79BDB0" }, { name: "Sky", hex: "#89AEDB" },
] as const;
export const GLASS_SAVE_KEY = "party-stack-stained-glass-v1";
// Grow around the original coordinate space so saved glass keeps its exact geometry.
export const SCENE = { width: 1260, height: 1420, presentationHeight: 1720, originX: -330, center: 300, left: -270, right: 870, top: 50, spring: 330, bottom: 1360, radius: 570, archHeight: 280 };
export type Point = { x: number; y: number };
export type GlassIntent = Point & { shape: GlassShape; color: string; size: number; rotation: number };
export type GlassPiece = GlassIntent & { player: number; playerId?: string };
export interface GlassState {
  version: 1;
  players: string[];
  selectedPlayer: number;
  goal: string | null;
  pieces: GlassPiece[];
  title: string;
  finished: boolean;
}

export function cleanGlassText(value: unknown, limit: number, fallback: string): string {
  const text = typeof value === "string" ? value.replace(/[\u0000-\u001f\u007f]/g, "").trim() : "";
  return Array.from(text || fallback).slice(0, limit).join("");
}

export function newGlassState(players = ["Player 1", "Player 2"]): GlassState {
  return { version: 1, players: normalizePlayers(players), selectedPlayer: 0, goal: null, pieces: [], title: "Our little light", finished: false };
}

export function normalizePlayers(players: unknown): string[] {
  const names = (Array.isArray(players) ? players : []).slice(0, 6)
    .map((name, index) => cleanGlassText(name, 24, `Player ${index + 1}`));
  while (names.length < 2) names.push(`Player ${names.length + 1}`);
  return names;
}

export function shapePoints(shape: GlassShape): Point[] {
  const coordinates: Record<Exclude<GlassShape, "circle">, number[][]> = {
    triangle: [[0, -.58], [.52, .32], [-.52, .32]],
    rectangle: [[-.55, -.35], [.55, -.35], [.55, .35], [-.55, .35]],
    diamond: [[0, -.65], [.43, 0], [0, .65], [-.43, 0]],
    hexagon: [[-.5, 0], [-.25, -.433], [.25, -.433], [.5, 0], [.25, .433], [-.25, .433]],
    leaf: [[-.50, .20], [-.12, -.48], [.45, -.32], [.38, .27], [-.08, .52]],
    shard: [[-.20, -.67], [.42, -.19], [.23, .56], [-.46, .31]],
    pebble: [[-.51, -.18], [-.24, -.44], [.26, -.36], [.53, .05], [.25, .43], [-.30, .38]],
  };
  return shape === "circle"
    ? Array.from({ length: 96 }, (_, index) => ({ x: Math.cos(index * Math.PI / 48) * .5, y: Math.sin(index * Math.PI / 48) * .5 }))
    : coordinates[shape].map(([x, y]) => ({ x, y }));
}

export function piecePoints(piece: GlassIntent): Point[] {
  const angle = piece.rotation * Math.PI / 180;
  const cos = Math.cos(angle), sin = Math.sin(angle);
  return shapePoints(piece.shape).map(({ x, y }) => ({
    x: piece.x + piece.size * (x * cos - y * sin),
    y: piece.y + piece.size * (x * sin + y * cos),
  }));
}

// A finely tessellated convex arch allows deterministic, host-independent area validation.
// Canvas uses the corresponding exact arc for clipping and native circles for drawing.
const aperture = [
  { x: SCENE.left, y: SCENE.bottom },
  ...Array.from({ length: 193 }, (_, i) => {
    const angle = Math.PI + i * Math.PI / 192;
    return { x: SCENE.center + SCENE.radius * Math.cos(angle), y: SCENE.spring + SCENE.archHeight * Math.sin(angle) };
  }),
  { x: SCENE.right, y: SCENE.bottom },
];
const cross = (a: Point, b: Point, p: Point) => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);

export function clippedGlassArea(piece: GlassIntent): number {
  let output = piecePoints(piece);
  for (let i = 0; i < aperture.length && output.length; i++) {
    const a = aperture[i], b = aperture[(i + 1) % aperture.length];
    const input = output;
    output = [];
    let previous = input.at(-1)!;
    let previousDistance = cross(a, b, previous);
    for (const current of input) {
      const distance = cross(a, b, current);
      if ((distance >= 0) !== (previousDistance >= 0)) {
        const t = previousDistance / (previousDistance - distance);
        output.push({ x: previous.x + t * (current.x - previous.x), y: previous.y + t * (current.y - previous.y) });
      }
      if (distance >= 0) output.push(current);
      previous = current;
      previousDistance = distance;
    }
  }
  return Math.abs(output.reduce((area, p, i) => {
    const next = output[(i + 1) % output.length];
    return area + p.x * next.y - next.x * p.y;
  }, 0)) / 2;
}

export function validGlassIntent(value: unknown): value is GlassIntent {
  if (!value || typeof value !== "object") return false;
  const p = value as GlassIntent;
  return GLASS_SHAPES.includes(p.shape) && GLASS_COLORS.some(c => c.hex === p.color) &&
    [p.x, p.y, p.size, p.rotation].every(Number.isFinite) &&
    Math.abs(p.x) <= SCENE.width + 230 && Math.abs(p.y) <= SCENE.presentationHeight + 230 &&
    p.size >= 45 && p.size <= 230 && p.rotation >= 0 && p.rotation <= 360 && clippedGlassArea(p) > .5;
}

export function placeGlass(state: GlassState, intent: GlassIntent, player = state.selectedPlayer): GlassState | null {
  if (state.finished || !validGlassIntent(intent) || !Number.isInteger(player) || player < 0 || player >= state.players.length) return null;
  return { ...state, pieces: [...state.pieces, { ...intent, player }] };
}

export function undoGlass(state: GlassState): GlassState {
  if (!state.pieces.length) return state;
  return { ...state, pieces: state.pieces.slice(0, -1), finished: false };
}

export function changeGlassPlayers(state: GlassState, players: string[]): GlassState {
  const names = normalizePlayers(players);
  return { ...state, players: names, selectedPlayer: state.selectedPlayer % names.length };
}

export function parseGlassSave(raw: string | null): GlassState {
  try {
    const value = JSON.parse(raw || "null") as (Partial<GlassState> & { turn?: number }) | null;
    if (!value || value.version !== 1 || !Array.isArray(value.pieces)) return newGlassState();
    const players = normalizePlayers(value.players);
    const selected = value.selectedPlayer ?? value.turn;
    return {
      version: 1, players,
      selectedPlayer: Number.isInteger(selected) && selected! >= 0 && selected! < players.length ? selected! : 0,
      goal: validGlassGoal(value.goal) ? value.goal : null,
      title: cleanGlassText(value.title, 60, "Our little light"), finished: value.finished === true,
      pieces: value.pieces.filter(validGlassIntent).map(p => ({
        shape: p.shape, color: p.color, size: p.size, rotation: p.rotation, x: p.x, y: p.y,
        player: Number.isInteger(p.player) && p.player >= 0 && p.player < 6 ? p.player : 0,
      })),
    };
  } catch { return newGlassState(); }
}

export function scenePoint(rect: { width: number; height: number; left: number; top: number }, clientX: number, clientY: number): Point {
  const scale = Math.min(rect.width / SCENE.width, rect.height / SCENE.height);
  return {
    x: SCENE.originX + (clientX - rect.left - (rect.width - SCENE.width * scale) / 2) / scale,
    y: (clientY - rect.top - (rect.height - SCENE.height * scale) / 2) / scale,
  };
}
